import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';
import supabase from '../lib/supabaseClient';

const Settings = () => {
  const { currentColors } = useAdminTheme();
  const [settings, setSettings] = useState({
    siteName: 'BootMinds Elite',
    siteDescription: 'Leading GCC establishment and operational excellence platform',
    adminEmail: 'admin@bootminds.com',
    allowRegistration: true,
    requireEmailVerification: true,
    maintenanceMode: false,
    analyticsEnabled: true,
    emailNotifications: true
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [emailSettings, setEmailSettings] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: 'noreply@bootminds.com',
    fromName: 'BootMinds Elite'
  });

  const [backupSettings, setBackupSettings] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    retentionDays: 30,
    includeUserData: true,
    includeContent: true
  });

  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: '/assets/images/White_logo_-_no_background-1753075265135.png',
    faviconUrl: '/favicon.ico',
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
    accentColor: '#8b5cf6',
    brandColors: {
      blue: '#3b82f6',
      purple: '#8b5cf6',
      green: '#10b981',
      red: '#ef4444'
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, load from database
      // For now, using default values
      
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, save to database
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      setLoading(true);
      
      // Get all data for export
      const { data: users } = await supabase.from('user_profiles').select('*');
      const { data: resources } = await supabase.from('resources').select('*');
      const { data: blogs } = await supabase.from('blogs').select('*');
      const { data: webinars } = await supabase.from('webinars').select('*');
      const { data: learningPaths } = await supabase.from('learning_paths').select('*');
      
      const exportData = {
        exportDate: new Date().toISOString(),
        users: users || [],
        resources: resources || [],
        blogs: blogs || [],
        webinars: webinars || [],
        learningPaths: learningPaths || []
      };
      
      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bootminds-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      alert('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async () => {
    try {
      if (window.confirm('Are you sure you want to clear all cache? This may temporarily slow down the application.')) {
        // Clear browser cache
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
        
        // Clear localStorage (except auth)
        const authKeys = ['supabase.auth.token'];
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && !authKeys.includes(key)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        alert('Cache cleared successfully!');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('Error clearing cache: ' + error.message);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'branding', label: 'Logo & Branding', icon: 'Image' },
    { id: 'email', label: 'Email', icon: 'Mail' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'backup', label: 'Backup', icon: 'Database' },
    { id: 'payments', label: 'Payment Gateways', icon: 'CreditCard' },
    { id: 'advanced', label: 'Advanced', icon: 'Wrench' }
  ];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin" style={{ color: currentColors.primary }} />
        <span className="ml-2" style={{ color: currentColors.text }}>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
            Settings
          </h2>
          <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
            Configure platform settings and preferences
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={loading}
          className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex items-center"
          style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
        >
          {loading ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} className="mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div 
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full px-4 py-3 text-left border-b transition-colors duration-200 flex items-center`}
                style={{
                  backgroundColor: activeTab === tab.id ? currentColors.primary : 'transparent',
                  color: activeTab === tab.id ? '#ffffff' : currentColors.text,
                  borderColor: currentColors.border
                }}
              >
                <Icon name={tab.icon} size={16} className="mr-3" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div 
            className="rounded-xl border p-6"
            style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
          >
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  General Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Site Name
                    </label>
                    <input
                      type="text"
                      value={settings.siteName}
                      onChange={(e) => setSettings(prev => ({ ...prev, siteName: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Admin Email
                    </label>
                    <input
                      type="email"
                      value={settings.adminEmail}
                      onChange={(e) => setSettings(prev => ({ ...prev, adminEmail: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Site Description
                  </label>
                  <textarea
                    value={settings.siteDescription}
                    onChange={(e) => setSettings(prev => ({ ...prev, siteDescription: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: currentColors.background, 
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>



                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>Allow User Registration</p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>Allow new users to create accounts</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.allowRegistration}
                        onChange={(e) => setSettings(prev => ({ ...prev, allowRegistration: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>Maintenance Mode</p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>Display maintenance message to users</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.maintenanceMode}
                        onChange={(e) => setSettings(prev => ({ ...prev, maintenanceMode: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'branding' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  Logo & Branding Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Main Logo
                    </label>
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={brandingSettings.logoUrl}
                        onChange={(e) => setBrandingSettings(prev => ({ ...prev, logoUrl: e.target.value }))}
                        placeholder="Logo URL"
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                      <div 
                        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-opacity-10 hover:bg-muted-foreground transition-all duration-200"
                        style={{ borderColor: currentColors.border }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="logo-upload"
                        />
                        <label htmlFor="logo-upload" className="cursor-pointer">
                          <Icon name="Upload" size={24} className="mx-auto mb-2" style={{ color: currentColors.textMuted }} />
                          <p style={{ color: currentColors.textMuted }}>Click to upload logo</p>
                        </label>
                      </div>
                      {brandingSettings.logoUrl && (
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                          <img
                            src={brandingSettings.logoUrl}
                            alt="Logo Preview"
                            className="max-h-16 mx-auto"
                            onError={(e) => {
                              e.target.src = '/assets/images/no_image.png';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Favicon Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Favicon (16x16)
                    </label>
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={brandingSettings.faviconUrl}
                        onChange={(e) => setBrandingSettings(prev => ({ ...prev, faviconUrl: e.target.value }))}
                        placeholder="Favicon URL"
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                      <div 
                        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-opacity-10 hover:bg-muted-foreground transition-all duration-200"
                        style={{ borderColor: currentColors.border }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="favicon-upload"
                        />
                        <label htmlFor="favicon-upload" className="cursor-pointer">
                          <Icon name="Upload" size={24} className="mx-auto mb-2" style={{ color: currentColors.textMuted }} />
                          <p style={{ color: currentColors.textMuted }}>Click to upload favicon</p>
                        </label>
                      </div>
                      {brandingSettings.faviconUrl && (
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                          <img
                            src={brandingSettings.faviconUrl}
                            alt="Favicon Preview"
                            className="w-8 h-8 mx-auto"
                            onError={(e) => {
                              e.target.src = '/favicon.ico';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Brand Colors */}
                <div>
                  <h4 className="font-medium mb-4" style={{ color: currentColors.text }}>Brand Colors</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Primary Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={brandingSettings.primaryColor}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                          className="w-12 h-10 rounded-lg border cursor-pointer"
                          style={{ borderColor: currentColors.border }}
                        />
                        <input
                          type="text"
                          value={brandingSettings.primaryColor}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                          className="flex-1 px-3 py-2 rounded-lg border text-sm"
                          style={{ 
                            backgroundColor: currentColors.background, 
                            borderColor: currentColors.border,
                            color: currentColors.text
                          }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Secondary Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={brandingSettings.secondaryColor}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                          className="w-12 h-10 rounded-lg border cursor-pointer"
                          style={{ borderColor: currentColors.border }}
                        />
                        <input
                          type="text"
                          value={brandingSettings.secondaryColor}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                          className="flex-1 px-3 py-2 rounded-lg border text-sm"
                          style={{ 
                            backgroundColor: currentColors.background, 
                            borderColor: currentColors.border,
                            color: currentColors.text
                          }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Accent Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={brandingSettings.accentColor}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                          className="w-12 h-10 rounded-lg border cursor-pointer"
                          style={{ borderColor: currentColors.border }}
                        />
                        <input
                          type="text"
                          value={brandingSettings.accentColor}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                          className="flex-1 px-3 py-2 rounded-lg border text-sm"
                          style={{ 
                            backgroundColor: currentColors.background, 
                            borderColor: currentColors.border,
                            color: currentColors.text
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview Section */}
                <div>
                  <h4 className="font-medium mb-4" style={{ color: currentColors.text }}>Brand Preview</h4>
                  <div 
                    className="p-6 rounded-lg border"
                    style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      {brandingSettings.logoUrl && (
                        <img
                          src={brandingSettings.logoUrl}
                          alt="Logo"
                          className="h-12"
                          onError={(e) => {
                            e.target.src = '/assets/images/no_image.png';
                          }}
                        />
                      )}
                      <div>
                        <h5 className="text-lg font-bold" style={{ color: brandingSettings.primaryColor }}>
                          {settings.siteName}
                        </h5>
                        <p className="text-sm" style={{ color: currentColors.textMuted }}>
                          {settings.siteDescription}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 rounded-lg font-medium text-white"
                        style={{ backgroundColor: brandingSettings.primaryColor }}
                      >
                        Primary Button
                      </button>
                      <button
                        className="px-4 py-2 rounded-lg font-medium text-white"
                        style={{ backgroundColor: brandingSettings.secondaryColor }}
                      >
                        Secondary Button
                      </button>
                      <button
                        className="px-4 py-2 rounded-lg font-medium text-white"
                        style={{ backgroundColor: brandingSettings.accentColor }}
                      >
                        Accent Button
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={saveSettings}
                    disabled={loading}
                    className="px-6 py-2 rounded-lg font-medium text-white transition-all duration-200"
                    style={{ backgroundColor: currentColors.primary }}
                  >
                    {loading ? 'Saving...' : 'Save Branding Settings'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  Email Configuration
                </h3>

                {/* Admin Account Management */}
                <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                  <h4 className="font-medium mb-4" style={{ color: currentColors.text }}>Admin Account Management</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Change Admin Email
                      </label>
                      <input
                        type="email"
                        placeholder="new-admin@bootminds.com"
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Current Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter current password"
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter new password"
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                      style={{
                        backgroundColor: currentColors.primary,
                        color: '#ffffff'
                      }}
                    >
                      Update Email
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                      style={{
                        backgroundColor: '#10b981',
                        color: '#ffffff'
                      }}
                    >
                      Change Password
                    </button>
                  </div>
                </div>

                {/* Admin Access Management */}
                <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                  <h4 className="font-medium mb-4" style={{ color: currentColors.text }}>Grant Admin Access</h4>
                  <p className="text-sm mb-4" style={{ color: currentColors.textMuted }}>
                    Grant admin access to users by email. They will receive an auto-generated password.
                  </p>
                  
                  <div className="flex gap-3">
                    <input
                      type="email"
                      placeholder="user@example.com"
                      className="flex-1 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                    <select
                      className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="moderator">Moderator</option>
                    </select>
                    <button
                      className="px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                      style={{
                        backgroundColor: currentColors.primary,
                        color: '#ffffff'
                      }}
                    >
                      <Icon name="UserPlus" size={16} />
                      Grant Access
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtpHost}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, smtpHost: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                      placeholder="smtp.gmail.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      SMTP Port
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtpPort}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, smtpPort: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      From Email
                    </label>
                    <input
                      type="email"
                      value={emailSettings.fromEmail}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, fromEmail: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      From Name
                    </label>
                    <input
                      type="text"
                      value={emailSettings.fromName}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, fromName: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: currentColors.background, 
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  Security Settings
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>Require Email Verification</p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>Users must verify email before accessing content</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.requireEmailVerification}
                        onChange={(e) => setSettings(prev => ({ ...prev, requireEmailVerification: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                    <h4 className="font-medium mb-2" style={{ color: currentColors.text }}>
                      API Keys
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span style={{ color: currentColors.textSecondary }}>OpenAI API Key</span>
                        <span className="text-green-500 text-sm">Configured</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: currentColors.textSecondary }}>Supabase URL</span>
                        <span className="text-green-500 text-sm">Configured</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'backup' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  Backup & Export
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>Auto Backup</p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>Automatically backup data at regular intervals</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={backupSettings.autoBackup}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Backup Frequency
                      </label>
                      <select
                        value={backupSettings.backupFrequency}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, backupFrequency: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                        Retention (Days)
                      </label>
                      <input
                        type="number"
                        value={backupSettings.retentionDays}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, retentionDays: parseInt(e.target.value) }))}
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ 
                          backgroundColor: currentColors.background, 
                          borderColor: currentColors.border,
                          color: currentColors.text
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={exportData}
                      disabled={loading}
                      className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex items-center"
                      style={{ backgroundColor: currentColors.success, color: '#ffffff' }}
                    >
                      <Icon name="Download" size={16} className="mr-2" />
                      Export All Data
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  Payment Gateway Configuration
                </h3>
                
                <div className="space-y-4">
                  {/* Stripe Configuration */}
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                          <span className="text-white font-bold text-sm">S</span>
                        </div>
                        <div>
                          <h5 className="font-medium" style={{ color: currentColors.text }}>Stripe</h5>
                          <p className="text-sm" style={{ color: currentColors.textMuted }}>Credit cards, digital wallets</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-surface peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-border peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Publishable Key"
                        className="px-3 py-2 rounded border text-sm"
                        style={{ backgroundColor: currentColors.background, borderColor: currentColors.border, color: currentColors.text }}
                      />
                      <input
                        type="password"
                        placeholder="Secret Key"
                        className="px-3 py-2 rounded border text-sm"
                        style={{ backgroundColor: currentColors.background, borderColor: currentColors.border, color: currentColors.text }}
                      />
                    </div>
                  </div>

                  {/* PayPal Configuration */}
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                          <span className="text-white font-bold text-sm">P</span>
                        </div>
                        <div>
                          <h5 className="font-medium" style={{ color: currentColors.text }}>PayPal</h5>
                          <p className="text-sm" style={{ color: currentColors.textMuted }}>PayPal payments and subscriptions</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-surface peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-border peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Client ID"
                        className="px-3 py-2 rounded border text-sm"
                        style={{ backgroundColor: currentColors.background, borderColor: currentColors.border, color: currentColors.text }}
                      />
                      <input
                        type="password"
                        placeholder="Client Secret"
                        className="px-3 py-2 rounded border text-sm"
                        style={{ backgroundColor: currentColors.background, borderColor: currentColors.border, color: currentColors.text }}
                      />
                    </div>
                  </div>

                  {/* Razorpay Configuration */}
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-800 rounded flex items-center justify-center">
                          <span className="text-white font-bold text-sm">R</span>
                        </div>
                        <div>
                          <h5 className="font-medium" style={{ color: currentColors.text }}>Razorpay</h5>
                          <p className="text-sm" style={{ color: currentColors.textMuted }}>Indian payment gateway</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-surface peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-border peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Key ID"
                        className="px-3 py-2 rounded border text-sm"
                        style={{ backgroundColor: currentColors.background, borderColor: currentColors.border, color: currentColors.text }}
                      />
                      <input
                        type="password"
                        placeholder="Key Secret"
                        className="px-3 py-2 rounded border text-sm"
                        style={{ backgroundColor: currentColors.background, borderColor: currentColors.border, color: currentColors.text }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                    style={{
                      backgroundColor: currentColors.primary,
                      color: '#ffffff'
                    }}
                  >
                    Save Payment Settings
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: currentColors.text }}>
                  Advanced Settings
                </h3>
                
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                    <h4 className="font-medium mb-2" style={{ color: currentColors.text }}>
                      Cache Management
                    </h4>
                    <p className="text-sm mb-4" style={{ color: currentColors.textMuted }}>
                      Clear application cache to resolve performance issues
                    </p>
                    <button
                      onClick={clearCache}
                      className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
                      style={{ backgroundColor: currentColors.warning, color: '#ffffff' }}
                    >
                      <Icon name="Trash2" size={16} className="mr-2" />
                      Clear Cache
                    </button>
                  </div>

                  <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}>
                    <h4 className="font-medium mb-2" style={{ color: currentColors.text }}>
                      System Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: currentColors.textSecondary }}>Platform Version</span>
                        <span style={{ color: currentColors.text }}>1.0.0</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: currentColors.textSecondary }}>Environment</span>
                        <span style={{ color: currentColors.text }}>Development</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: currentColors.textSecondary }}>Last Updated</span>
                        <span style={{ color: currentColors.text }}>{new Date().toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
